import { computed, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useProductCompleteStore } from '@/features/Product/stores/productComplete';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import type { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type {
  CheckoutProductSelection,
  MysteryProductDetails,
  ProductComplete,
} from '@/features/Product/types/models';

export const useMysteryProduct = (mysteryDetails: Ref<MysteryProductDetails | undefined>) => {
  const { cachedDisplayProducts, cachedCompleteProducts } = storeToRefs(useProductCompleteStore());
  const { completeBaseProducts } = storeToRefs(useProductBaseStore());
  const { isProductSoldOut } = useProductStockStore();
  const productSources = computed(() => [
    ...completeBaseProducts.value,
    ...cachedCompleteProducts.value,
    ...cachedDisplayProducts.value,
  ]);

  const isMysteryProduct = (product: ProductComplete) => {
    const details = mysteryDetails.value;
    if (!details) return false;
    return (
      product.attributes.sku === details.baseSku ||
      product.attributes.sku?.startsWith(details.baseSku) ||
      product.attributes.handle === details.productHandle
    );
  };

  const isMysteryEnabled = computed(() => !!mysteryDetails.value?.baseSku);

  const isMysteryProductAvailable = (product: ProductComplete) => {
    if (!isMysteryProduct(product)) return !isProductSoldOut(product);
    const details = mysteryDetails.value;
    if (!details?.productHandle) return !isProductSoldOut(product);
    return productSources.value.some(
      (p) => p.attributes.handle === details.productHandle && !isProductSoldOut(p),
    );
  };

  const resolveVariantIdsInBundleDetails = (
    bundleDetails: CheckoutProductSelection[],
  ): CheckoutProductSelection[] => {
    const details = mysteryDetails.value;
    if (!isMysteryEnabled.value || !details?.productHandle) return bundleDetails;
    return bundleDetails.map((item) => {
      const resolvedProduct = productSources.value.find(
        (p) => p.attributes.sku === item.sku && p.attributes.handle === details.productHandle,
      );
      if (!resolvedProduct) return item;
      return {
        ...item,
        id: `gid://shopify/ProductVariant/${resolvedProduct.attributes.variantId}`,
      };
    });
  };

  const resolveMysteryAtcObject = (items: AddToCartItem[]): AddToCartItem[] => {
    if (!isMysteryEnabled.value) return items;
    return items.map((item) => {
      const customBundleDetails = item.properties?._custom_bundle_details;
      if (!customBundleDetails) return item;
      try {
        const resolvedDetails = resolveVariantIdsInBundleDetails(JSON.parse(customBundleDetails));
        return {
          ...item,
          properties: {
            ...item.properties,
            _custom_bundle_details: JSON.stringify(resolvedDetails),
          },
        };
      } catch {
        return item;
      }
    });
  };

  return {
    isMysteryEnabled,
    isMysteryProduct,
    isMysteryProductAvailable,
    resolveVariantIdsInBundleDetails,
    resolveMysteryAtcObject,
  };
};
